/**
 * useCanvasEventHandlers - Unified event handlers for canvas editors
 *
 * Provides mode-aware event handlers that work for block and screen canvas editors.
 * Handlers route events to flowCanvasEventHandler with appropriate event types based on mode.
 *
 * @param mode - Canvas mode ('blocks' | 'screens')
 * @param eventHandlerContext - Context for event handler (coordinator, commandManager, setters)
 * @param registry - Entity registry (blockRegistry or screenRegistry)
 * @param edgeDialogState - State management for edge-related dialogs
 * @returns Event handler callbacks
 */
import { useCallback } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { Edge } from '@xyflow/react';
import { flowCanvasEventHandler } from '@modules/reactFlow/services/shared/FlowCanvasEventHandler';
import type { CanvasMode } from './useCanvasEditorState';
import type { EntityRegistry } from '@modules/reactFlow/services/shared/EntityRegistry';

const uniqueIds = (ids: string[]): string[] => Array.from(new Set(ids.filter(Boolean)));

export interface EventHandlerContext {
  setSelectedScreenIds: Dispatch<SetStateAction<string[]>>;
  setIsSyncing: Dispatch<SetStateAction<boolean>>;
  setSyncOperation: Dispatch<SetStateAction<string>>;
  coordinator: any;
  commandManager: any;
  logEvent: () => void;
  showConfirmation: (message: string, details?: string) => Promise<boolean>;
}

export interface EdgeDialogState {
  setEditorOpen: (open: boolean) => void;
  setSelectedEdge: (edge: Edge | null) => void;
  setShowEdgeDeleteConfirm: (show: boolean) => void;
  setEdgesToDelete: (edgeIds: string[]) => void;
  edgesToDelete: string[];
}

export interface CanvasEventHandlers {
  handleScreensSelected: (entityIds: string[], additive: boolean) => Promise<void>;
  handleScreensMoved: (movedEntities: Array<{ entityId: string; newPosition: { x: number; y: number } }>) => Promise<void>;
  handleConnectionCompleted: (payload: { sourceEntityId: string; targetEntityId: string; sourceActionKey: string }) => Promise<void>;
  handleScreenDropped: (payload: { templateId: string; position: { x: number; y: number }; snapToGrid: boolean }) => Promise<void>;
  handleScreensDeleted: (entityIds: string[]) => Promise<void>;
  handleEdgesDeleted: (edgeIds: string[]) => Promise<void>;
  confirmEdgeDeletion: () => Promise<void>;
  handleEdgeClicked: (edgeId: string, edgeData: any) => Promise<void>;
  handleEdgeDeleteFromDialog: (edgeId: string) => Promise<void>;
  handleScreenSaved: (updatedScreen: any) => void;
}

/**
 * Hook for creating mode-aware event handlers
 */
export const useCanvasEventHandlers = (
  mode: CanvasMode,
  eventHandlerContext: EventHandlerContext | null,
  registry: EntityRegistry<any>,
  edgeDialogState: EdgeDialogState,
): CanvasEventHandlers => {
  // Handle entity selection
  const handleScreensSelected = useCallback(async (entityIds: string[], additive: boolean) => {
    if (!eventHandlerContext) return;

    const eventType = mode === 'blocks' ? 'block.selected' : 'screen.selected';
    const eventPayload = mode === 'blocks'
      ? {
          type: eventType as 'block.selected',
          timestamp: Date.now(),
          blockIds: entityIds,
          additive,
        }
      : {
          type: eventType as 'screen.selected',
          timestamp: Date.now(),
          screenIds: entityIds,
          additive,
        };

    await flowCanvasEventHandler.handleEvent(eventPayload, eventHandlerContext);
  }, [mode, eventHandlerContext]);

  // Handle entity movement
  const handleScreensMoved = useCallback(async (movedEntities: Array<{ entityId: string; newPosition: { x: number; y: number } }>) => {
    if (!eventHandlerContext) return;

    const eventType = mode === 'blocks' ? 'block.moved' : 'screen.moved';
    const eventPayload = mode === 'blocks'
      ? {
          type: eventType as 'block.moved',
          timestamp: Date.now(),
          movedBlocks: movedEntities.map(e => ({ blockId: e.entityId, newPosition: e.newPosition })),
        }
      : {
          type: eventType as 'screen.moved',
          timestamp: Date.now(),
          movedScreens: movedEntities.map(e => ({ screenId: e.entityId, newPosition: e.newPosition })),
        };

    await flowCanvasEventHandler.handleEvent(eventPayload, eventHandlerContext);
  }, [mode, eventHandlerContext]);

  // Handle connections (unified for both modes)
  const handleConnectionCompleted = useCallback(async (payload: { sourceEntityId: string; targetEntityId: string; sourceActionKey: string }) => {
    if (!eventHandlerContext) return;
    await flowCanvasEventHandler.handleEvent({
      type: 'edge.created',
      timestamp: Date.now(),
      sourceEntityId: payload.sourceEntityId,
      targetEntityId: payload.targetEntityId,
      sourceActionKey: payload.sourceActionKey,
    }, eventHandlerContext);
  }, [eventHandlerContext]);

  // Handle entity drops
  const handleScreenDropped = useCallback(async (payload: { templateId: string; position: { x: number; y: number }; snapToGrid: boolean }) => {
    if (!eventHandlerContext) return;

    const eventType = mode === 'blocks' ? 'block.created' : 'screen.created';
    const eventPayload = mode === 'blocks'
      ? {
          type: eventType as 'block.created',
          timestamp: Date.now(),
          blockData: { templateId: payload.templateId },
          position: payload.position,
        }
      : {
          type: eventType as 'screen.created',
          timestamp: Date.now(),
          templateId: payload.templateId,
          position: payload.position,
        };

    await flowCanvasEventHandler.handleEvent(eventPayload, eventHandlerContext);
  }, [mode, eventHandlerContext]);

  // Handle entity deletion
  const handleScreensDeleted = useCallback(async (entityIds: string[]) => {
    if (!eventHandlerContext) return;

    const uniqueEntityIds = uniqueIds(entityIds);
    if (uniqueEntityIds.length === 0) return;

    const eventType = mode === 'blocks' ? 'block.deleted' : 'screen.deleted';
    const eventPayload = mode === 'blocks'
      ? {
          type: eventType as 'block.deleted',
          timestamp: Date.now(),
          blockIds: uniqueEntityIds,
        }
      : {
          type: eventType as 'screen.deleted',
          timestamp: Date.now(),
          screenIds: uniqueEntityIds,
        };

    await flowCanvasEventHandler.handleEvent(eventPayload, eventHandlerContext);
  }, [mode, eventHandlerContext]);

  // Handle edge deletion (shows confirmation dialog)
  const handleEdgesDeleted = useCallback(async (edgeIds: string[]) => {
    const uniqueEdgeIds = uniqueIds(edgeIds);
    if (uniqueEdgeIds.length > 0) {
      edgeDialogState.setEdgesToDelete(uniqueEdgeIds);
      edgeDialogState.setShowEdgeDeleteConfirm(true);
    }
  }, [edgeDialogState]);

  // Confirm edge deletion (called from confirmation dialog)
  const confirmEdgeDeletion = useCallback(async () => {
    if (!eventHandlerContext) return;
    await flowCanvasEventHandler.handleEvent({
      type: 'edge.deleted',
      timestamp: Date.now(),
      edgeIds: edgeDialogState.edgesToDelete,
    }, eventHandlerContext);
    edgeDialogState.setShowEdgeDeleteConfirm(false);
    edgeDialogState.setEdgesToDelete([]);
  }, [edgeDialogState, eventHandlerContext]);

  // Handle edge clicked (opens TransitionEditor)
  const handleEdgeClicked = useCallback(async (edgeId: string, edgeData: any) => {
    if (!eventHandlerContext) return;
    await flowCanvasEventHandler.handleEvent({
      type: 'edge.clicked',
      timestamp: Date.now(),
      edgeId,
      edgeData,
    }, eventHandlerContext);

    const sourceScreen = registry.get(edgeData.sourceEntityId);
    const targetScreen = registry.get(edgeData.targetEntityId);

    edgeDialogState.setSelectedEdge({
      id: edgeId,
      source: edgeData.sourceEntityId,
      target: edgeData.targetEntityId,
      data: {
        ...edgeData,
        sourceScreen,
        targetScreen,
      },
    } as Edge);
    edgeDialogState.setEditorOpen(true);
  }, [eventHandlerContext, registry, edgeDialogState]);

  // Handle edge delete from TransitionEditor dialog
  const handleEdgeDeleteFromDialog = useCallback(async (edgeId: string) => {
    if (!eventHandlerContext) return;
    await flowCanvasEventHandler.handleEvent({
      type: 'edge.deleted',
      timestamp: Date.now(),
      edgeIds: [edgeId],
    }, eventHandlerContext);
  }, [eventHandlerContext]);

  // Handle screen saved (update registry)
  const handleScreenSaved = useCallback((updatedScreen: any) => {
    registry.register(updatedScreen as any);
  }, [registry]);

  return {
    handleScreensSelected,
    handleScreensMoved,
    handleConnectionCompleted,
    handleScreenDropped,
    handleScreensDeleted,
    handleEdgesDeleted,
    confirmEdgeDeletion,
    handleEdgeClicked,
    handleEdgeDeleteFromDialog,
    handleScreenSaved,
  };
};
